import { t } from "i18next";
import { DownloadButton } from "../../components/common/DownloadButton";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { WellInputs } from "./WellInputs";
import { useCallback } from "react";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import { getDataForIPR } from "../../store/slices/pvtCalculatorSlice";
import { CalculateButton } from "../../components/common/CalculateButton";
import { validateInputs } from "../../store/slices/iprCalculatorSlice";
import { OuterData } from "../../components/common/OuterData";
import type { IIPRWell } from "./interfaces";
import type { IDisplayField } from "../../interfaces/interfaces";
import { getSecondWellIsOpen } from "../../store/slices/appSlice";

const validated = (well: IIPRWell) => {
	const inputs = well.calculationType === "horizontal" ? well.horizontalInputs :
		well.calculationType === "vertical" ? well.verticalInputs :
			well.calculationType === "jones" ? well.jonesInputs :
				[];

	return !inputs.some(input => input.error);
}

const validatedData = ({ inputs, errors }: { inputs: IDisplayField[]; errors: string[] }) => {
	return !inputs.some(input => input.error) && errors.length === 0;
}

export const InputSection = () => {
	const secondWell = useAppSelector(getSecondWellIsOpen);
	const well1 = useAppSelector(state => state.iprCalculator.well1);
	const well2 = useAppSelector(state => state.iprCalculator.well2);

	const [triggerCalculator] = useCalculatorMutation();
	const pvtData = useAppSelector(getDataForIPR);
	const dispatch = useAppDispatch();

	const getFormData = useCallback((wellId: "well1" | "well2") => {
		dispatch(validateInputs());

		const well = wellId === "well1" ? well1 : well2;
		if (!well || !validated(well) || !validatedData({ inputs: pvtData.inputs, errors: pvtData.errors })) {
			return;
		}
		const inputs = well.calculationType === "horizontal" ? well.horizontalInputs :
			well.calculationType === "vertical" ? well.verticalInputs :
				well.calculationType === "jones" ? well.jonesInputs :
					[];
		const formData: Record<string, number | string> = {};

		[...inputs, ...pvtData.inputs].forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});
		formData['well'] = wellId === "well1" ? "1" : "2";

		return formData;
	}, [well1, well2, pvtData]);

	const onCalculate = useCallback(async () => {
		if (!validated(well1) || !validatedData({ inputs: pvtData.inputs, errors: pvtData.errors })) {
			return;
		}

		if (secondWell && (!validated(well2) || !validatedData({ inputs: pvtData.inputs, errors: pvtData.errors }))) {
			return;
		}

		const formDataWell1 = getFormData("well1");
		if (formDataWell1) {
			triggerCalculator({
				calculator_type: "ipr",
				well: "1",
				...formDataWell1,
			});
		}

		if (secondWell) {
			const formDataWell2 = getFormData("well2");
			if (formDataWell2) {
				triggerCalculator({
					calculator_type: "ipr",
					well: "2",
					...formDataWell2,
				});
			}
		}
	}, [well1, well2, pvtData, getFormData, triggerCalculator, secondWell]);

	return <div className="ipr__input-section">
		<div className="ipr__wells">
			{<WellInputs well={well1} />}
			{secondWell && <WellInputs well={well2} />}
		</div>
		<div className="ipr__buttons">
			<CalculateButton onClick={onCalculate} disabled={false} />
			<DownloadButton getFormData={() => getFormData("well1")} wellId="well1" calculatorType="ipr" disabled={false} title={`${t(`download`)} (${t(`ipr:well1_title`)})`} />
			{secondWell && (
				<DownloadButton getFormData={() => getFormData("well2")} wellId="well2" calculatorType="ipr" disabled={false} title={`${t(`download`)} (${t(`ipr:well2_title`)})`} />
			)}
		</div>
		<OuterData
			translationNamespace="pvt"
			inputs={pvtData.inputs}
			errors={pvtData.errors} />
	</div>;
}